// src/pages/ConfirmLocation.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLocationHandling } from '../LocationHandling/useLocationHandling';
import api from '../../../shared/api/axios';
import { useAuth } from '../../../shared/contexts/AuthContext';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { useTranslation } from 'react-i18next';

// Fix leaflet default marker assets for bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const btnStyle: React.CSSProperties = {
  padding: '12px 24px',
  backgroundColor: 'rgb(56, 6, 117)',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  fontSize: '16px',
  fontWeight: 'bold',
  marginTop: '20px',
};

type RouterState = {
  media?: string | string[];    // one or many absolute URLs to your media (R2/Worker, CF Stream, etc.)
  description?: string;         // optional caption
};

const ConfirmLocation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { state } = useRouterLocation() as { state: RouterState | undefined };
  const { location } = useLocationHandling(); // your shared location (may include countryCode if you added it)

  const [loading, setLoading] = useState(false);     // nothing async to load by default
  const [posting, setPosting] = useState(false);

  // Optional local inputs (pre-filled from router state)
  const [description, setDescription] = useState<string>(state?.description ?? '');
  const [media, setMedia] = useState<string | string[]>(state?.media ?? '');

  // If you want to reflect late-arriving router state updates:
  useEffect(() => {
    if (state?.media !== undefined) setMedia(state.media);
    if (state?.description !== undefined) setDescription(state.description);
  }, [state?.media, state?.description]);

  const handlePost = async () => {
    // Basic validation
    const hasMedia =
      (typeof media === 'string' && media.trim().length > 0) ||
      (Array.isArray(media) && media.length > 0);

      const invalidLocation =
  !location.city ||
  !location.country ||
  location.city === 'Unknown' ||
  location.country === 'Unknown';

if (posting || !hasMedia || invalidLocation) {

        console.warn('❗', t('confirmLocation.alerts.missingMediaOrLocation'));
        return;
    }

    setPosting(true);
    try {
      if (!isAuthenticated) {
        alert(t('confirmLocation.alerts.loginRequired'));
        return;
      }

      // Normalize to array of URLs
      const mediaArr = Array.isArray(media) ? media.filter(Boolean) : [media].filter(Boolean);

      // Build payload for /media route
      const payload = {
        media: mediaArr,
        location: {
          city: location.city,
          country: location.country,
          // If your LocationContext now includes an ISO code, send it through.
          // Your backend already normalizes/derives countryCode if missing.
          countryCode: (location as any).countryCode,
        },
        captions: description ? [description] : [],
        // hashtags: [] // add if you have a UI for it
      };
      const safeLocation = {
  city: location.city,
  country: location.country,
  countryCode: location.countryCode,
};

      await api.post('/media', payload);

      alert(t('confirmLocation.alerts.postSuccess'));
      sessionStorage.setItem('selectedLocation', JSON.stringify(safeLocation));
      navigate('/gallery', { state: { location: safeLocation } });  
    } catch (err) {
      console.error('❌ Error posting:', err);
      alert(t('confirmLocation.alerts.postFailed'));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={{ padding: '40px', textAlign: 'center', marginTop: '80px' }}>
      <h2>📍 {t('confirmLocation.title')}</h2>

      {loading ? (
        <p>{t('confirmLocation.loading')}</p>
      ) : (
        <>
          <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
  {location.city
    ? t('confirmLocation.locationKnown', {
        city: location.city,
        country: location.country,
      })
    : t('confirmLocation.locationUnknown')}
</p>


        {location.coords && (
  <MapContainer
    center={location.coords}
    zoom={14}
    scrollWheelZoom
    style={{ height: '300px', width: '100%', maxWidth: '600px', margin: '0 auto' }}
  >
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution="&copy; OpenStreetMap contributors"
    />
    <Marker position={location.coords} />
  </MapContainer>
)}

          {/* Optional: small caption field (kept simple) */}
          {/* Remove this block if you don't want editing here */}
          <div style={{ maxWidth: 600, margin: '16px auto 0' }}>
            <textarea
              placeholder={t('confirmLocation.captionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{
                width: '100%',
                minHeight: 80,
                padding: 12,
                borderRadius: 8,
                border: '1px solid #ccc',
                resize: 'vertical',
              }}
            />
          </div>

          <button onClick={handlePost} style={btnStyle} disabled={posting}>
             ✅ {posting ? t('confirmLocation.buttons.posting') : t('confirmLocation.buttons.confirmAndPost')}
          </button>
        </>
      )}
    </div>
  );
};

export default ConfirmLocation;
